/**
 * Switch Profile Editor Modal
 *
 * Form for creating/editing custom switch profiles.
 * Simplified version focusing on essential fields.
 */

import { useState } from 'react';
import { saveCustomSwitchProfile, type CustomSwitchProfile } from '../../services/customProfileStorage';
import type { SwitchModel, SwitchFabricType, SwitchRole, SwitchVendor } from '../../types/switches';

interface SwitchProfileEditorProps {
  profile: CustomSwitchProfile | null;
  onClose: (saved: boolean) => void;
}

export function SwitchProfileEditor({ profile, onClose }: SwitchProfileEditorProps) {
  const isEdit = profile !== null;

  // Simplified state focusing on essential fields
  const [formData, setFormData] = useState({
    id: profile?.id || '',
    name: profile?.name || '',
    productName: profile?.productName || '',
    vendor: profile?.vendor || ('Custom' as SwitchVendor),
    fabricType: profile?.fabricType || ('Ethernet' as SwitchFabricType),
    typicalRoles: profile?.typicalRoles || (['leaf'] as SwitchRole[]),

    // Port configuration
    totalPorts: profile?.ports.totalPorts || 64,
    speedValue: profile?.ports.speed.value || 100,
    speedUnit: profile?.ports.speed.unit || ('Gbps' as 'Gbps' | 'Tbps'),
    speedDisplayName: profile?.ports.speed.displayName || '100GbE',
    portType: profile?.ports.portType || ('QSFP28' as const),
    supportsBreakout: profile?.ports.supportsBreakout || false,

    // Power
    typicalWatts: profile?.power.typicalWatts || 500,
    maxWatts: profile?.power.maxWatts || 650,
    idleWatts: profile?.power.idleWatts || 200,
    perPortWatts: profile?.power.perPortWatts || 5,

    // Physical
    rackUnits: profile?.physical.rackUnits || 1,
    widthMm: profile?.physical.widthMm || 440,
    depthMm: profile?.physical.depthMm || 600,
    weightKg: profile?.physical.weightKg || 15,
    airflow: profile?.physical.airflow || ('front-to-back' as const),

    // Performance (simplified)
    switchingCapacityTbps: profile?.performance.switchingCapacityTbps || 12.8,
    forwardingRateMpps: profile?.performance.forwardingRateMpps || 9000,
    latencyNs: profile?.performance.latencyNs || 600,
    bufferSizeMB: profile?.performance.bufferSizeMB || 32,
  });

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (role: SwitchRole) => {
    setFormData(prev => {
      const roles = prev.typicalRoles.includes(role)
        ? prev.typicalRoles.filter(r => r !== role)
        : [...prev.typicalRoles, role];
      return { ...prev, typicalRoles: roles };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.id || !formData.name || !formData.productName) {
      alert('Please fill in all required fields (ID, Name, Product Name)');
      return;
    }

    if (formData.typicalRoles.length === 0) {
      alert('Please select at least one typical role');
      return;
    }

    // Build complete switch model
    const switchModel: SwitchModel = {
      id: formData.id,
      name: formData.name,
      productName: formData.productName,
      vendor: formData.vendor,
      fabricType: formData.fabricType,
      typicalRoles: formData.typicalRoles,
      ports: {
        totalPorts: formData.totalPorts,
        speed: {
          value: formData.speedValue,
          unit: formData.speedUnit,
          displayName: formData.speedDisplayName,
        },
        portType: formData.portType,
        supportsBreakout: formData.supportsBreakout,
      },
      power: {
        typicalWatts: formData.typicalWatts,
        maxWatts: formData.maxWatts,
        idleWatts: formData.idleWatts,
        perPortWatts: formData.perPortWatts,
        psuConfig: {
          count: 2,
          capacity: Math.ceil(formData.maxWatts / 2),
          redundancy: 'N+1',
        },
      },
      physical: {
        rackUnits: formData.rackUnits,
        widthMm: formData.widthMm,
        depthMm: formData.depthMm,
        weightKg: formData.weightKg,
        airflow: formData.airflow,
        maxBtuPerHour: Math.round(formData.maxWatts * 3.412),
      },
      performance: {
        switchingCapacityTbps: formData.switchingCapacityTbps,
        forwardingRateMpps: formData.forwardingRateMpps,
        latencyNs: formData.latencyNs,
        bufferSizeMB: formData.bufferSizeMB,
      },
      features: {
        layer2: true,
        layer3: formData.typicalRoles.includes('spine'),
        vxlan: false,
        evpn: false,
        rdma: formData.fabricType === 'RoCE',
        adaptiveRouting: formData.fabricType === 'InfiniBand',
        telemetry: true,
        inBandManagement: true,
        oobManagement: true,
      },
      metadata: {
        notes: 'Custom switch profile',
      },
    };

    try {
      saveCustomSwitchProfile(switchModel);
      onClose(true);
    } catch (error) {
      alert('Failed to save switch profile: ' + error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Edit Switch Profile' : 'Create Switch Profile'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.id}
                  onChange={(e) => handleChange('id', e.target.value)}
                  disabled={isEdit}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="unique-switch-id"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Unique identifier (cannot be changed)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="Model number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.productName}
                  onChange={(e) => handleChange('productName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="Marketing/product name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.vendor}
                  onChange={(e) => handleChange('vendor', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  required
                >
                  <option value="NVIDIA">NVIDIA</option>
                  <option value="Arista">Arista</option>
                  <option value="Cisco">Cisco</option>
                  <option value="Juniper">Juniper</option>
                  <option value="Broadcom">Broadcom</option>
                  <option value="Dell">Dell</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fabric Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.fabricType}
                  onChange={(e) => handleChange('fabricType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  required
                >
                  <option value="InfiniBand">InfiniBand</option>
                  <option value="Ethernet">Ethernet</option>
                  <option value="RoCE">RoCE</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typical Roles <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(['leaf', 'spine', 'super-spine', 'edge', 'management'] as SwitchRole[]).map(role => (
                    <label key={role} className="flex items-center space-x-1">
                      <input
                        type="checkbox"
                        checked={formData.typicalRoles.includes(role)}
                        onChange={() => handleRoleToggle(role)}
                        className="w-4 h-4"
                      />
                      <span className="text-xs">{role}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Port Configuration */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Port Configuration</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Ports <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.totalPorts}
                  onChange={(e) => handleChange('totalPorts', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Port Speed <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.speedValue}
                  onChange={(e) => handleChange('speedValue', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                  step="0.1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Speed Unit <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.speedUnit}
                  onChange={(e) => handleChange('speedUnit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  required
                >
                  <option value="Gbps">Gbps</option>
                  <option value="Tbps">Tbps</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.speedDisplayName}
                  onChange={(e) => handleChange('speedDisplayName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="100GbE, XDR, etc."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Port Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.portType}
                  onChange={(e) => handleChange('portType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  required
                >
                  <option value="QSFP-DD">QSFP-DD</option>
                  <option value="QSFP56">QSFP56</option>
                  <option value="QSFP28">QSFP28</option>
                  <option value="OSFP">OSFP</option>
                  <option value="SFP+">SFP+</option>
                  <option value="SFP28">SFP28</option>
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.supportsBreakout}
                    onChange={(e) => handleChange('supportsBreakout', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">Supports Breakout</span>
                </label>
              </div>
            </div>
          </div>

          {/* Power Specifications */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Power Specifications</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typical (W) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.typicalWatts}
                  onChange={(e) => handleChange('typicalWatts', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max (W) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.maxWatts}
                  onChange={(e) => handleChange('maxWatts', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Idle (W)</label>
                <input
                  type="number"
                  value={formData.idleWatts}
                  onChange={(e) => handleChange('idleWatts', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Per Port (W)</label>
                <input
                  type="number"
                  value={formData.perPortWatts}
                  onChange={(e) => handleChange('perPortWatts', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="0.1"
                  step="0.1"
                />
              </div>
            </div>
          </div>

          {/* Physical Specifications */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Physical Specifications</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rack Units <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.rackUnits}
                  onChange={(e) => handleChange('rackUnits', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                  max="10"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Width (mm)</label>
                <input
                  type="number"
                  value={formData.widthMm}
                  onChange={(e) => handleChange('widthMm', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Depth (mm)</label>
                <input
                  type="number"
                  value={formData.depthMm}
                  onChange={(e) => handleChange('depthMm', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                <input
                  type="number"
                  value={formData.weightKg}
                  onChange={(e) => handleChange('weightKg', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="0.1"
                  step="0.1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Airflow</label>
                <select
                  value={formData.airflow}
                  onChange={(e) => handleChange('airflow', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="front-to-back">Front-to-Back</option>
                  <option value="back-to-front">Back-to-Front</option>
                  <option value="side-to-side">Side-to-Side</option>
                </select>
              </div>
            </div>
          </div>

          {/* Performance Specifications */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Specifications</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Switching Capacity (Tbps)
                </label>
                <input
                  type="number"
                  value={formData.switchingCapacityTbps}
                  onChange={(e) => handleChange('switchingCapacityTbps', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="0.1"
                  step="0.1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forwarding Rate (Mpps)
                </label>
                <input
                  type="number"
                  value={formData.forwardingRateMpps}
                  onChange={(e) => handleChange('forwardingRateMpps', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Latency (ns)
                </label>
                <input
                  type="number"
                  value={formData.latencyNs}
                  onChange={(e) => handleChange('latencyNs', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buffer Size (MB)
                </label>
                <input
                  type="number"
                  value={formData.bufferSizeMB}
                  onChange={(e) => handleChange('bufferSizeMB', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded hover:bg-indigo-700"
            >
              {isEdit ? 'Save Changes' : 'Create Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
